import React, { useContext, useEffect } from 'react';
import {
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Container,
  useTheme,
  alpha,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import UserProfileMenu from '../../components/UserProfileMenu';
import SiteInfoTab from './SiteInfoTab';
import ContactsSection from './ContactsSection';
import HealthTargetCard from './HealthTargetCard';

/**
 * 현장 설정 페이지.
 *
 * 기존: 기본 정보 / 네트워크-IP 관리 두 개 탭.
 * 신규: 기본 정보 단일 페이지로 단순화. 네트워크/IP 관리는 사이드바
 * 'IP / 네트워크' 메뉴에서 스프레드시트로 관리.
 *
 * 기본 정보는 read-only 카드 → "수정" 클릭 시 인라인 폼으로 전환되어
 * 직접 편집 가능 (PUT /v0.1/site-management/projects/{id}).
 */
const SiteSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();

  const selectedProject = localStorage.getItem('selectedProject');
  const projectData = selectedProject ? JSON.parse(selectedProject) : null;
  const projectIdNum = projectId ? Number(projectId) : NaN;

  // ContactsBar 등에서 ?tab=contacts 로 진입하면 담당자 섹션으로 스크롤.
  // (이 페이지는 탭이 아니라 세로 스택 구조라 쿼리만으로는 위치가 안 잡힘)
  const focusSection = searchParams.get('tab');
  useEffect(() => {
    if (!focusSection) return;
    const id = focusSection === 'contacts' ? 'contacts' : focusSection;
    // 렌더 직후 레이아웃이 안정된 다음 스크롤.
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [focusSection]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/project/${projectId}`)} size="small" aria-label="뒤로가기">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate(`/project/${projectId}`)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {projectData?.name || `프로젝트 ${projectId}`}
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}
            >
              <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 설정
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <UserProfileMenu />
        </Box>
      </Paper>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {Number.isFinite(projectIdNum) ? (
          <>
            <SiteInfoTab projectId={projectIdNum} cachedProject={projectData} />
            <HealthTargetCard projectId={projectIdNum} />
            <ContactsSection projectId={projectIdNum} />
          </>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography color="error">유효하지 않은 프로젝트 ID</Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default SiteSettingsPage;
